import { useSelector } from 'react-redux';
import { betreuerByNrSelector, skontodefinitionenByArtSelector } from 'store/lookups/selector';
import { Rechnung, RechnungFilter, updateRechnung, useRechnungList } from 'api/rechnung';
import * as React from 'react';
import { useBetreuerprovisionListByNr } from 'api/betreuerprovision';
import { RechnungPositionFilter, useRechnungPositionList } from 'api/rechnungPosition';
import { AuftragFilter, useAuftragList } from 'api/auftrag';
import { getBetreuerAnteil } from 'reports/auftragsuebersicht/utils';
import { RechnungSummary, defaultLineData, FilterValues, LineData } from '../types';

export interface RechnungSummaryProps extends FilterValues {
    enabled: boolean;
}

export const useRechnungSummary = ({
    abrJahr,
    abrMo,
    currentMonth,
    betreuer,
}: RechnungSummaryProps): RechnungSummary => {
    const betreuerByNr = useSelector(betreuerByNrSelector);
    const betreuerprovisionListByNr = useBetreuerprovisionListByNr();
    const skontodefinitionenByArt = useSelector(skontodefinitionenByArtSelector);

    const rechnungFilter = React.useMemo<RechnungFilter>(() => {
        if (currentMonth) {
            return {
                abrJahr: 0,
                abrMo: 0,
                status: 6,
                betreuerExact: betreuer,
                withKunde: true,
            };
        }

        return {
            abrJahr,
            abrMo,
            betreuerExact: betreuer,
            withKunde: true,
        };
    }, [abrJahr, abrMo, betreuer, currentMonth]);

    const rechnungList = useRechnungList(rechnungFilter);

    const auftragFilter = React.useMemo<AuftragFilter>(
        () => ({ auNrList: (rechnungList.data || []).map((r) => r.auNr).filter((auNr) => !!auNr) }),
        [rechnungList.data],
    );

    const auftragList = useAuftragList(auftragFilter, { enabled: !!rechnungList.data });

    const rechnungPositionFilter = React.useMemo<RechnungPositionFilter>(
        () => ({ rNrList: rechnungList.data?.map((r) => r.nr) || [] }),
        [rechnungList],
    );
    const rechnungPositionList = useRechnungPositionList(rechnungPositionFilter, { enabled: !!rechnungList.data });

    const finish = React.useCallback(async (): Promise<string> => {
        if (!rechnungList.data || rechnungList.data.length === 0) {
            return 'Keine Rechnungen ausgewählt.';
        }

        try {
            // eslint-disable-next-line no-restricted-syntax
            for (const rechnung of rechnungList.data) {
                const finishedRechnung: Rechnung = {
                    ...rechnung,
                    abrJahr,
                    abrMo,
                };

                // eslint-disable-next-line no-await-in-loop
                await updateRechnung(finishedRechnung);
            }

            rechnungList.refetch().then();
        } catch (e) {
            return 'Fehler: ' + e.message;
        }

        return (rechnungList.data?.length || 0) + ' Rechnungen abgeschlossen';
    }, [rechnungList, abrJahr, abrMo]);

    return React.useMemo<RechnungSummary>(() => {
        const total: LineData = { ...defaultLineData };
        const betreuerTotal: {
            [key: number]: LineData;
        } = {};

        const rechnungLineDataList: LineData[] = [];

        (rechnungList.data || [])
            .sort((a, b) => (a.datum + a.nr).localeCompare(b.datum + b.nr))
            .forEach((rechnung) => {
                if (![6].includes(rechnung.status) || rechnung.rahmen) {
                    return;
                }

                const filteredRechnungPositionList = (rechnungPositionList.data || []).filter(
                    (ap) => ap.rNr === rechnung.nr,
                );

                if (filteredRechnungPositionList.length === 0) {
                    return;
                }

                const auftrag = rechnung.auNr ? (auftragList.data || []).find((a) => a.nr === rechnung.auNr) : null;

                const rechnungLineData: LineData = {
                    ...defaultLineData,
                    rNr: rechnung.nr,
                    auNr: rechnung.auNr,
                    datum: rechnung.datum,
                    bezDat: rechnung.bezDat,
                    kNr: rechnung.kNr || 0,
                    kName: rechnung.kunde?.name1 || '',
                };

                const skontoP = skontodefinitionenByArt[rechnung.zahlBed]?.skP1 || 0;

                let ekNetto = 0;
                let vkNetto = 0;

                filteredRechnungPositionList.forEach((rechnungPosition) => {
                    ekNetto += rechnungPosition.posEkPreis * rechnungPosition.posMenge;
                    vkNetto += rechnungPosition.posEPreis * rechnungPosition.posMenge;
                });

                const tipp = rechnung.tipp === 0 ? 0 : (vkNetto / 100) * rechnung.tipp;
                const skonto = skontoP === 0 ? 0 : (vkNetto / 100) * skontoP;
                const bMarge = vkNetto - ekNetto;
                const nMarge = bMarge - tipp - skonto - rechnung.fracht - rechnung.roadPr;

                const { value: bAnteil } = getBetreuerAnteil(
                    betreuerprovisionListByNr,
                    rechnung.betreuer,
                    rechnung.datum,
                    (betreuerTotal[rechnung.betreuer]?.nMarge || 0) + nMarge,
                    betreuerTotal[rechnung.betreuer]?.bAnteil || 0,
                );
                const fAnteil = nMarge - bAnteil;

                total.count += 1;

                total.ekNetto += ekNetto;
                rechnungLineData.ekNetto = ekNetto;

                total.vkNetto += vkNetto;
                rechnungLineData.vkNetto = vkNetto;

                total.bMarge += bMarge;
                rechnungLineData.bMarge = bMarge;

                total.tippProv += tipp;
                rechnungLineData.tippProv = tipp;

                total.skonto += skonto;
                rechnungLineData.skonto = skonto;

                total.fracht += rechnung.fracht + rechnung.roadPr;
                rechnungLineData.fracht = rechnung.fracht + rechnung.roadPr;

                total.nMarge += nMarge;
                rechnungLineData.nMarge = nMarge;

                total.bAnteil += bAnteil;
                rechnungLineData.bAnteil = bAnteil;

                total.fAnteil += fAnteil;
                rechnungLineData.fAnteil = fAnteil;

                if (auftrag?.betrUm) {
                    total.betrUmCount += 1;
                    total.betrUm += vkNetto;
                }

                if (rechnung.betreuer) {
                    if (!betreuerTotal[rechnung.betreuer]) {
                        betreuerTotal[rechnung.betreuer] = {
                            ...defaultLineData,
                            name: betreuerByNr[rechnung.betreuer]?.name || betreuerByNr[rechnung.betreuer]?.kz,
                        };
                    }

                    betreuerTotal[rechnung.betreuer].betreuerNr = rechnung.betreuer;
                    betreuerTotal[rechnung.betreuer].count += 1;
                    betreuerTotal[rechnung.betreuer].ekNetto += ekNetto;
                    betreuerTotal[rechnung.betreuer].vkNetto += vkNetto;
                    betreuerTotal[rechnung.betreuer].bMarge += bMarge;
                    betreuerTotal[rechnung.betreuer].tippProv += tipp;
                    betreuerTotal[rechnung.betreuer].skonto += skonto;
                    betreuerTotal[rechnung.betreuer].fracht += rechnung.fracht + rechnung.roadPr;
                    betreuerTotal[rechnung.betreuer].nMarge += nMarge;
                    betreuerTotal[rechnung.betreuer].bAnteil += bAnteil;
                    betreuerTotal[rechnung.betreuer].fAnteil += fAnteil;
                }

                rechnungLineDataList.push(rechnungLineData);
            });

        let fAnteilTotal = 0;
        Object.values(betreuerTotal).forEach((item) => {
            const provision = betreuerByNr[item.betreuerNr]?.provision || 0;
            if (provision >= 100) {
                fAnteilTotal += item.bAnteil;
            } else {
                fAnteilTotal += item.fAnteil;
            }
        });

        return {
            loading: rechnungList.isLoading,
            count: total.count, // rechnungList.data?.length || 0,
            total,
            betreuerTotal,
            rechnungLineDataList,
            fAnteilTotal,
            finish,
        };
    }, [
        betreuerprovisionListByNr,
        rechnungList.isLoading,
        rechnungList.data,
        auftragList.data,
        rechnungPositionList.data,
        skontodefinitionenByArt,
        betreuerByNr,
        finish,
    ]);
};
